import { useState } from "react";
import { Crosshair, CloudRain, Sparkle, ShareNetwork, Path } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

export const useWhere = () => {
  const [where, setWhere] = useState(null);
  const [busy, setBusy] = useState(false);
  const take = () => new Promise((resolve) => {
    if (!navigator.geolocation) {
      toast.error("This device will not give us a position — type the place instead");
      return resolve(null);
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const point = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      try {
        const { data } = await api.post("/rides/where", point);
        setWhere(data);
        toast.success(`You are at ${data.name}`);
        resolve(data);
      } catch (e) { oops(e); setWhere(point); resolve(point); }
      finally { setBusy(false); }
    }, (err) => {
      setBusy(false);
      toast.error(err.code === 1
        ? "You said no to location — type the place instead"
        : "Could not get a position just now — type the place instead");
      resolve(null);
    }, { enableHighAccuracy: true, timeout: 10000 });
  });
  return { where, take, busy };
};

export const GpsBar = ({ where, onTake, busy, onFrom, onTo, onNear }) => (
  <div className="border border-border bg-white p-4" data-testid="gps-box">
    <p className="overline text-slate-500">
      <Crosshair size={13} className="mr-1 inline align-[-2px] text-[#B3241B]" /> Where you are
    </p>
    <div className="mt-2 flex flex-wrap gap-2">
      <Button size="sm" variant="outline" data-testid="gps-take" disabled={busy} onClick={onTake}>
        <Crosshair size={14} className="mr-1.5" /> Take my position
      </Button>
      {where && (
        <>
          <Button size="sm" variant="outline" data-testid="gps-set-from" onClick={() => onFrom(where)}>
            Set as pick-up
          </Button>
          <Button size="sm" variant="outline" data-testid="gps-set-to" onClick={() => onTo(where)}>
            Set as drop
          </Button>
          <Button size="sm" variant="outline" data-testid="gps-near" onClick={() => onNear(where)}>
            Runs near me
          </Button>
        </>
      )}
    </div>
    {where && (
      <p className="mt-2 text-sm text-slate-700" data-testid="gps-place">
        {where.name || `${where.lat.toFixed(4)}, ${where.lng.toFixed(4)}`}
      </p>
    )}
  </div>
);

export const Planner = ({ where, onApply }) => {
  const [ask, setAsk] = useState("I need to be at Pit 4 for seven in the morning");
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="border border-border bg-white p-4" data-testid="planner-box">
      <p className="overline text-slate-500">
        <Sparkle size={13} className="mr-1 inline align-[-2px] text-[#002FA7]" /> Let AI set it up
      </p>
      <div className="mt-2 grid gap-2 sm:grid-cols-[2fr_auto]">
        <Input data-testid="planner-ask" placeholder="Where do you need to be, and when?"
          value={ask} onChange={(e) => setAsk(e.target.value)} />
        <Button size="sm" data-testid="planner-go" disabled={busy || ask.trim().length < 5}
          onClick={async () => {
            setBusy(true);
            try {
              const { data } = await api.post("/rides/plan", {
                ask, lat: where?.lat, lng: where?.lng });
              setPlan(data);
              toast.success("Set up — check it over and post it");
            } catch (e) { oops(e); } finally { setBusy(false); }
          }}>
          Set it up
        </Button>
      </div>
      {plan && (
        <div className="mt-3 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2" data-testid="planner-out">
          <p className="text-sm text-[#0F172A]">{plan.line}</p>
          <p className="mt-1 text-xs text-slate-600">
            {plan.from_place} → {plan.to_place} · set off {plan.when} · {plan.miles} miles ·
            {" "}{plan.vehicle}
            {plan.leave_earlier_minutes
              ? ` · leave ${plan.leave_earlier_minutes} minutes earlier for the weather`
              : ""}
            {(plan.stops || []).length ? ` · via ${plan.stops.join(", ")}` : ""}
          </p>
          {plan.weather?.warning && (
            <p className="mt-1 text-xs font-semibold text-[#B3241B]" data-testid="planner-warning">
              {plan.weather.warning}
            </p>
          )}
          <Button className="mt-2" size="sm" variant="outline" data-testid="planner-apply"
            onClick={() => { onApply(plan); toast.success("Dropped into the form"); }}>
            Use this
          </Button>
        </div>
      )}
    </div>
  );
};

export const RideJourney = ({ rideId }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" data-testid={`journey-${rideId}`} disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            const { data } = await api.get(`/rides/${rideId}/journey`);
            setInfo(data);
          } catch (e) { oops(e); } finally { setBusy(false); }
        }}>
        <CloudRain size={14} className="mr-1.5" /> Road and weather
      </Button>
      {info && (
        <p className="mt-1 w-full text-xs text-slate-600" data-testid={`journey-out-${rideId}`}>
          <Path size={12} className="mr-1 inline align-[-2px]" />
          {info.route ? `${info.route.miles} miles, about ${info.route.minutes} minutes` : "route unavailable"}
          {info.setting_off
            ? ` · ${Math.round(info.setting_off.temperature_c)}°C, ${info.setting_off.rain_mm ?? 0}mm rain, gusts ${Math.round(info.setting_off.gusts_kmh ?? 0)} km/h at ${info.setting_off.at?.slice(11, 16)}`
            : ""}
          {info.warning && (
            <span className="font-semibold text-[#B3241B]"> · {info.warning}</span>
          )}
          <span className="block text-[0.65rem] text-slate-400">{info.attribution}</span>
        </p>
      )}
    </>
  );
};

export const CompanionPost = ({ rideId }) => {
  const [post, setPost] = useState(null);
  return (
    <>
      <Button size="sm" variant="outline" data-testid={`companion-post-${rideId}`}
        onClick={async () => {
          try {
            const { data } = await api.post(`/rides/${rideId}/companion-post`);
            setPost(data);
            await navigator.clipboard.writeText(`${data.text} ${data.url}`).catch(() => {});
            toast.success("Words copied — or tap Facebook below");
          } catch (e) { oops(e); }
        }}>
        <ShareNetwork size={14} className="mr-1.5" /> Find me a companion
      </Button>
      {post && (
        <span className="flex w-full flex-wrap items-center gap-2 text-xs" data-testid={`companion-post-out-${rideId}`}>
          <span className="text-slate-600">{post.text}</span>
          <a href={post.facebook} target="_blank" rel="noreferrer"
            className="font-semibold text-[#002FA7] underline" data-testid={`companion-post-fb-${rideId}`}>
            Facebook
          </a>
          <a href={post.whatsapp} target="_blank" rel="noreferrer"
            className="font-semibold text-[#0B5C2E] underline">WhatsApp</a>
          <a href={post.x} target="_blank" rel="noreferrer"
            className="font-semibold text-slate-700 underline">X</a>
          <a href={post.linkedin} target="_blank" rel="noreferrer"
            className="font-semibold text-slate-700 underline">LinkedIn</a>
        </span>
      )}
    </>
  );
};
